import { Badge, Input, Item, ItemActions, ItemHeader, ItemTitle } from '@ibsys/kit';
import { CheckIcon, PencilIcon, XIcon } from 'lucide-react';
import { FC, KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SampleListItem } from '@/entities/sample';
import { cn } from '@/shared/lib';
import { ConfirmationButton, IconButton, TruncatedText } from '@/shared/ui';

type Props = {
  sample: SampleListItem;
  /** Выбранная выборка — подсвечивается */
  isActive: boolean;
  /** Есть разрешение на изменение выборок: переименование и удаление */
  canAdmin: boolean;
  onApply: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
};

/**
 * Строка выборки в перечне: выбор по клику, размер выборки и её тип у правого края,
 * inline-переименование и удаление с подтверждением.
 */
export const SampleItem: FC<Props> = ({ sample, isActive, canAdmin, onApply, onRename, onDelete }) => {
  const { t } = useTranslation(['common', 'samples']);
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(sample.name);

  const startEdit = useCallback(() => {
    setDraftName(sample.name);
    setIsEditing(true);
  }, [sample.name]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setDraftName(sample.name);
  }, [sample.name]);

  const commitEdit = useCallback(() => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== sample.name) onRename(trimmed);
    setIsEditing(false);
  }, [draftName, sample.name, onRename]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
    },
    [commitEdit, cancelEdit],
  );

  if (isEditing) {
    return (
      <Item variant="outline" size="sm" className="p-2">
        <ItemHeader>
          <Input
            autoFocus
            size="sm"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
          />
          <ItemActions className="ml-2 shrink-0">
            <IconButton
              size="icon"
              label={t('samples:saveName')}
              icon={<CheckIcon className="size-3.5" />}
              onClick={commitEdit}
            />
            <IconButton
              size="icon"
              label={t('samples:cancelRename')}
              icon={<XIcon className="size-3.5" />}
              onClick={cancelEdit}
            />
          </ItemActions>
        </ItemHeader>
      </Item>
    );
  }

  return (
    <Item
      variant="outline"
      size="sm"
      role="button"
      tabIndex={-1}
      onClick={onApply}
      className={cn(
        'group cursor-pointer hover:bg-muted/60 p-2 w-full min-w-0',
        isActive && 'border-primary bg-primary/5',
      )}
    >
      <ItemHeader className="min-w-0 min-h-9">
        <ItemTitle className="block w-full min-w-0 text-sm">
          <TruncatedText>{sample.name}</TruncatedText>
        </ItemTitle>
        {/* Размер и тип стоят вне блока действий: попадание в них выбирает выборку, а гасит клик
            только сам блок кнопок. Обе колонки одной ширины во всех строках — перечень читается
            столбцами, а не вперемешку с названиями. */}
        <span className="w-16 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
          {sample.conversationCount != null &&
            t(sample.type === 'filters' ? 'samples:size.approx' : 'samples:size.exact', {
              value: sample.conversationCount,
            })}
        </span>
        {/* Ширина задана минимумом: собственную `w-fit` бейджа классом ширины не снять */}
        <Badge variant="secondary" outline className="min-w-16 shrink-0 justify-center">
          {t(sample.type === 'filters' ? 'samples:type.filters' : 'samples:type.records')}
        </Badge>
        {canAdmin && (
          <ItemActions className="ml-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            {/* Не `hidden`: появляясь, кнопки сдвигали содержимое строки под курсором, и клик,
                нацеленный в тип или число диалогов, попадал по «Переименовать». */}
            <div className="invisible group-hover:visible group-focus-within:visible flex items-center gap-2">
              <IconButton
                size="icon"
                label={t('samples:rename')}
                icon={<PencilIcon className="size-3.5" />}
                onClick={startEdit}
              />
              <ConfirmationButton
                label={t('samples:delete')}
                title={t('samples:deleteConfirmTitle', { name: sample.name })}
                description={t('samples:deleteConfirmDescription')}
                onConfirm={onDelete}
                trigger={{ size: 'icon' }}
              />
            </div>
          </ItemActions>
        )}
      </ItemHeader>
    </Item>
  );
};
